import React from 'react';

import { MenuItem } from '@mui/material';
import { widgetsConfigurations } from 'config/widgetConfigurations/widgetsConfigurations';
import { FormProvider } from 'react-hook-form';
import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseTable } from 'hooks/useBaseTable';
import { useBaseWidget } from 'hooks/useBaseWidget';

import { ColumnsManager } from 'components/common/ColumnsManager/ColumnsManager';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { TableContainer } from 'components/common/Tables/components/TableContainer/TableContainer';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetExistInBackend } from 'components/common/WidgetExistInBackend/WidgetExistInBackend';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';

import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { ITableWidgetWrapperProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { AddToReport } from 'components/Dashboards/Reporting/AddToReport/AddToReport';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { Narrative } from 'components/Dashboards/Reporting/WidgetNarrativeWrapper/Narrative';
import { PinboardPills } from 'components/Dashboards/Reporting/pinboardPills/PinboardPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { useKeyActivitiesLogic } from 'components/widgets/KeyActivities/KeyActivities.logic';
import { useKeyActivitiesProvider } from 'components/widgets/KeyActivities/KeyActivities.provider';
import { columnsV8, initialState } from 'components/widgets/KeyActivities/KeyActivities.utils';
import { Filter } from 'components/widgets/KeyActivities/components/filter';
import { ExportToCsvButton } from 'components/widgets/RiskRegister/components/ExportToCsvButton/ExportToCsvButton';
import classesGeneralWidget from 'components/widgets/Utils/Styles/widget.module.scss';

const { componentKey, id, endpoint } = widgetsConfigurations.keyActivities;

export const KeyActivities = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    compareVersionId,
    globalFilterData,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    localFilters,

    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title = widgetsConfigurations.keyActivities.title,
}: ITableWidgetWrapperProps) => {
    const { table } = useBaseTable({
        widgetId,
        localFilters,
        componentKey,
        initialState,
        columns: columnsV8,
        widgetViewMode,
    });

    const { ui, api } = useKeyActivitiesProvider({
        componentKey,
        widgetId,
        contractId,
        latestVersionId,
        compareVersionId,
        globalFilterData,
        localFilters,
        widgetViewMode,
    });

    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
        ui,
        reportWidgetRef,
        onAddWidgetCallback: table.setWidgetColumns,
        apiPayload: api.getKeyActivities.payload,
        widgetEndpoint: endpoint,
    });

    const { logic } = useKeyActivitiesLogic({ globalFilterData, api, ui, table });

    return (
        <WidgetExistInBackend componentKey={componentKey}>
            <WidgetWithTitle
                id={id}
                title={
                    <WidgetTitle
                        title={editableTitle}
                        setTitle={setEditableTitle}
                        widgetViewMode={widgetViewMode}
                        postfix={logic.counter}
                        showPostfixInEditMode={true}
                    />
                }
                className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
                titleFilters={[
                    <ReportingVersionAndPills
                        key={'ReportingVersionAndPills'}
                        selectedProjectAndContract={widgetSelectedProjectAndContract}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                    <Narrative key={'narrative'} editNarrative={editNarrative} />,
                    <PinboardPills
                        key={'pinboardPills'}
                        isInPinboard={ui.isInPinboard}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                    WidgetViewModeUtils.isPageOrReportAddOrEditWidget(widgetViewMode) && (
                        <FormProvider {...ui.form} key={'form'}>
                            <Filter
                                key={'filters'}
                                contractId={contractId}
                                projectId={projectId}
                                widgetViewMode={widgetViewMode}
                            />
                        </FormProvider>
                    ),
                ]}
                titleComponents={[
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    <AddWidget
                        key={'AddWidget'}
                        componentKey={componentKey}
                        title={title}
                        projectId={projectId}
                        contractId={contractId}
                        widgetId={widgetId}
                        route={route}
                        localFilters={ui.state}
                        callBack={table.setWidgetColumns}
                        shouldHide={true}
                        shouldUseNewIcon={true}
                    />,
                    <AddToReport
                        key={'addWidgetToReport'}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        projectId={projectId}
                        contractId={contractId}
                        uiState={ui.state}
                        shouldHide={WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)}
                        callBack={table.setWidgetColumns}
                        apiPayload={api.getKeyActivities.payload}
                        widgetEndpoint={endpoint}
                    />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ExportDropdown key={'exportDropDown'} title={title} subTitle={subTitle}>
                            <MenuItem>
                                <ExportToCsvButton
                                    headers={logic.exportCsv.headers}
                                    rows={logic.exportCsv.rows}
                                    fileName={logic.exportCsv.fileName}
                                />
                            </MenuItem>
                        </ExportDropdown>
                    ),
                    !WidgetViewModeUtils.isReportView(widgetViewMode) && (
                        <ColumnsManager
                            onOpenModal={() => table.setModalColumnsOpen(true)}
                            onHideModal={() => table.setModalColumnsOpen(false)}
                            showModal={table.modalColumnsOpen}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            tableInstance={table.tableInstance}
                            initialState={initialState}
                            columns={columnsV8}
                            onChangeOrder={table.setColumnOrder}
                            onChangeVisibility={table.setColumnVisibility}
                            widgetViewMode={widgetViewMode}
                        />
                    ),
                    <TooltipIcon key={'info'} tooltip={api.getKeyActivities.instance.data?.info} />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ThreeDotsMenu
                            widgetViewMode={widgetViewMode}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            shareTitle={title}
                            shareLink={route}
                            projectId={projectId}
                            contractId={contractId}
                        />
                    ),
                ]}
            >
                {table.updatedInitialState && (
                    <TableContainer>
                        <TableV8
                            data={logic.filteredData}
                            columns={columnsV8}
                            noData={logic.noDataText}
                            isLoading={api.getKeyActivities.instance.isFetching}
                            sorting={table.sorting}
                            setSorting={table.setSorting}
                            columnFilters={table.columnFilters}
                            setColumnFilters={table.setColumnFilters}
                            columnVisibility={table.columnVisibility}
                            setColumnVisibility={table.setColumnVisibility}
                            columnOrder={table.columnOrder}
                            setColumnOrder={table.setColumnOrder}
                            manualSorting={false}
                            rowSelection={table.rowSelection}
                            setRowSelection={table.setRowSelection}
                            ref={table.tableRef}
                            tableClassName={
                                WidgetViewModeUtils.isReportView(widgetViewMode)
                                    ? classesGeneralWidget.reportTableScrollable
                                    : classesGeneralWidget.pageTableScrollable
                            }
                            wrapperClassName={
                                WidgetViewModeUtils.isInAnyReportViewOrAddMode(widgetViewMode)
                                    ? classesGeneralWidget.reportTableWrapper
                                    : ''
                            }
                        />
                    </TableContainer>
                )}
            </WidgetWithTitle>
        </WidgetExistInBackend>
    );
};
